import React from "react";

interface AutoNavProps {
  enabled: boolean;
  dedupeKey: string;
}

interface UseAutoNavigationParams {
  auto?: boolean;
  autoNavProps?: AutoNavProps;
  entityId?: string;
  time?: bigint;
  isReady: boolean;
  onNavigate: () => void | boolean;
}

/**
 * Hook to handle automatic navigation with deduplication.
 *
 * This hook manages the logic for automatically triggering navigation actions
 * when certain conditions are met, while preventing duplicate navigations
 * using session storage.
 */
export function useAutoNavigation({
  auto,
  autoNavProps,
  entityId,
  time,
  isReady,
  onNavigate,
}: UseAutoNavigationParams) {
  // Effect to trigger autonavigation
  React.useEffect(() => {
    if (!auto || !autoNavProps?.enabled || !isReady || !entityId) {
      return;
    }

    // Build dedupe key
    let fullDedupeKey = entityId;
    if (time) {
      fullDedupeKey += `-${time}`;
    }
    fullDedupeKey += autoNavProps.dedupeKey;

    // Check if already followed
    if (sessionStorage.getItem(fullDedupeKey) === "followed") {
      return;
    }

    // Execute navigation
    const result = onNavigate();

    // Mark as followed (for VizInternalLink, only if successful)
    if (result === undefined || result) {
      sessionStorage.setItem(fullDedupeKey, "followed");
    }
  }, [auto, autoNavProps, entityId, time, isReady, onNavigate]);

  // Helper to mark navigation as followed (for manual clicks)
  const markAsFollowed = React.useCallback(
    (markEntityId: string, markTime?: bigint): void => {
      if (!auto || !autoNavProps?.enabled) {
        return;
      }

      let fullDedupeKey = markEntityId;
      if (markTime) {
        fullDedupeKey += `-${markTime}`;
      }
      fullDedupeKey += autoNavProps.dedupeKey;
      sessionStorage.setItem(fullDedupeKey, "followed");
    },
    [auto, autoNavProps],
  );

  return { markAsFollowed };
}
